import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SkipLink from "@/components/SkipLink";
import SectionHeader from "@/components/SectionHeader";
import CTAButton from "@/components/CTAButton";
import { StructuredData, SEO } from "@/components/SEO";
import { ArrowRight } from "lucide-react";

export default function AboutPage() {
  const breadcrumbData = {
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://fiteasy.com",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "About",
        item: "https://fiteasy.com/about",
      },
    ],
  };

  return (
    <>
      <SEO
        title="About"
        description="Learn how FitEasy connects you with trainers, park classes, and nutrition experts to simplify your fitness journey. Fitness that fits your life, not the other way around."
        path="/about"
      />
      <StructuredData type="BreadcrumbList" data={breadcrumbData} />
      <div className="bg-gradient-to-b from-slate-50 to-blue-50 text-gray-900 min-h-screen">
        <SkipLink />
        <Header />

        <main id="main-content" className="mx-auto max-w-6xl px-4 pb-16 pt-10 sm:px-6 lg:px-8">
          <SectionHeader
            id="about-heading"
            label="About"
            title="Fitness that adapts to your life, not the other way around"
            description="FitEasy was born from a simple observation: most people want to be healthier, but the fitness industry makes it unnecessarily complicated. We've built a platform that removes the friction between wanting to be healthier and actually achieving your goals. By bringing together certified trainers, registered nutritionists, outdoor group classes, and comprehensive progress tracking in one seamless experience, we've made professional fitness guidance accessible, affordable, and effective for everyone—regardless of where you're starting from."
            className="mb-10"
          />

          <section aria-labelledby="about-heading" className="grid gap-8 md:grid-cols-3">
            <article className="rounded-3xl border border-blue-100 bg-white/90 p-6 transition hover:shadow-lg focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2">
              <h2 className="font-display text-lg text-gray-900">
                The problem we solve
              </h2>
              <p className="mt-2 text-sm text-gray-700 leading-relaxed">
                The fitness industry is fragmented and confusing. People struggle with generic workout apps that don&apos;t understand their unique needs, expensive gym memberships that go unused, conflicting nutrition advice, and no way to track progress meaningfully. It&apos;s hard to know who to trust, what program to follow, and how to stay consistent when life gets busy. Most people give up not because they lack willpower, but because the system is broken.
              </p>
            </article>
            <article className="rounded-3xl border border-blue-100 bg-white/90 p-6 transition hover:shadow-lg focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2">
              <h2 className="font-display text-lg text-gray-900">
                Our integrated solution
              </h2>
              <p className="mt-2 text-sm text-gray-700 leading-relaxed">
                FitEasy is the first platform that truly integrates everything you need: certified personal trainers, registered nutritionists, outdoor group classes, comprehensive progress tracking, and community support—all in one seamless app. No more juggling multiple subscriptions, apps, or memberships. Everything works together, from your workout plan to your meal plan to your progress metrics, giving you a complete picture of your health journey.
              </p>
            </article>
            <article className="rounded-3xl border border-blue-100 bg-white/90 p-6 transition hover:shadow-lg focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2">
              <h2 className="font-display text-lg text-gray-900">
                Our core philosophy
              </h2>
              <p className="mt-2 text-sm text-gray-700 leading-relaxed">
                We believe in real humans over generic algorithms, sustainable habits over crash transformations, and holistic health over just aesthetics. Every person is unique, and their fitness journey should reflect that. We&apos;re not here to sell you quick fixes or unrealistic promises. We&apos;re here to provide expert guidance, genuine support, and tools that help you build a healthier life—one that you can actually maintain and enjoy.
              </p>
            </article>
          </section>

          <section className="mt-12 rounded-3xl border border-blue-200 bg-white/90 p-8 text-center">
            <h2 className="font-display text-2xl text-gray-900 mb-4">
              Ready to start your fitness journey?
            </h2>
            <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
              Join thousands of members who are transforming their health with FitEasy.
            </p>
            <CTAButton
              href="/programs"
              variant="primary"
              ariaLabel="Get started with FitEasy"
            >
              Get Started
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </CTAButton>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}

